/**
 * Display logic over the server's forecast (§8).
 *
 * The split this file exists to hold: **the server decides *when*, the client
 * decides *how it reads*.** `GET /timeline` runs the projection engine and
 * hands back occurrences — dates, what governed them, where inside an event
 * they land. Nothing here recomputes any of that. What it does is turn those
 * facts into the words and groupings the mock's tiles show: which system a part
 * belongs under, whether a crossing reads as "at event" or "≈ 12 passes out",
 * and what a part's overall lamp colour is once its expiry, its inspection
 * window and its metric intervals are all considered together.
 *
 * This is the client half of the arrangement build-plan §8 asks for: "Keep the
 * projection *display* logic in the client but source occurrences from the API
 * so the server is the source of truth for the forecast."
 */

import { itemStatus } from '@svcdue/core';
import type { Status } from '@svcdue/core';
import type {
  CertOccurrence,
  ItemDto,
  System,
  TimelineOccurrence,
  TimelineResponse,
} from '@svcdue/contracts';

import { fmtShort } from './format';

const SEVERITY: Record<Status, number> = { good: 0, due: 1, over: 2 };

/** An item is only as healthy as its worst condition — same rule as the API's. */
export function worst(statuses: Status[]): Status {
  return statuses.reduce<Status>((acc, s) => (SEVERITY[s] > SEVERITY[acc] ? s : acc), 'good');
}

/**
 * Per-item status, rebuilt from what the timeline response actually carries.
 *
 * The API computes this internally (`statusByItem` in services/timeline.ts) but
 * does **not** put it on the wire — `TimelineResponse` carries occurrences,
 * certs and inspections, and no status map. So the client reassembles it from
 * those three, using the *same* shared rule from `@svcdue/core` the API used.
 * That is why this can be trusted to agree: it is not a second opinion, it is
 * the same function over the same server-computed inputs.
 *
 * Certs and inspections already arrive with a `status` decided server-side, so
 * they are read, not recomputed. Only the metric occurrences go back through
 * `itemStatus`, because that rule needs the car's next event date — which the
 * response gives us — to answer "due *before you race*" rather than "due
 * within some arbitrary window".
 */
export function statusByItem(timeline: TimelineResponse): Map<string, Status> {
  const byItem = new Map<string, Status[]>();
  const push = (itemId: string, status: Status) => {
    const list = byItem.get(itemId);
    if (list) list.push(status);
    else byItem.set(itemId, [status]);
  };

  for (const cert of timeline.certs) push(cert.itemId, cert.status);
  for (const inspection of timeline.inspections) push(inspection.itemId, inspection.status);

  // Occurrences arrive flattened across every item; regroup before judging,
  // since `itemStatus` reasons about one item's whole forecast at once.
  const occurrencesByItem = new Map<string, TimelineOccurrence[]>();
  for (const occurrence of timeline.occurrences) {
    const list = occurrencesByItem.get(occurrence.itemId);
    if (list) list.push(occurrence);
    else occurrencesByItem.set(occurrence.itemId, [occurrence]);
  }

  for (const [itemId, occurrences] of occurrencesByItem) {
    const carId = occurrences[0]?.carId ?? null;
    push(itemId, itemStatus({ occurrences, today: timeline.today, nextEventDate: nextEventDateFor(timeline, carId) }));
  }

  return new Map([...byItem].map(([itemId, statuses]) => [itemId, worst(statuses)]));
}

/** The next planned event for a car — the deadline "due soon" is measured against. */
function nextEventDateFor(timeline: TimelineResponse, carId: string | null): string | null {
  if (!carId) return null;
  return timeline.events.find((e) => e.carId === carId && e.status === 'planned' && e.date >= timeline.today)?.date ?? null;
}

/**
 * What a part's projection line reads as.
 *
 * Mirrors the mock's `proj` strings. The engine tells us *where* the crossing
 * lands and *what governed it*; this turns that into the sentence on the tile.
 */
export function projectionNote(occurrence: TimelineOccurrence | undefined, today: string): string {
  if (!occurrence) return 'No forecast — add a check condition';
  if (occurrence.overdue) return 'Overdue now';

  // A crossing partway through an event is the interesting case: it is the one
  // that earns a before/during choice, so say so rather than giving a date.
  if (occurrence.choice) return 'Crosses mid-event';
  if (occurrence.eventId) return `Due at ${fmtShort(occurrence.date)}`;

  const days = daysFrom(today, occurrence.date);
  return `≈ ${days}d out`;
}

function daysFrom(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

/**
 * Items grouped the way the garage screen lays them out.
 *
 * The order is the mock's, not alphabetical: it walks the car from the engine
 * outwards and ends at safety, which is how a racer thinks about the machine.
 * An empty system is dropped rather than rendered as a bare heading.
 */
const SYSTEM_ORDER: System[] = ['Engine', 'Power adder', 'Fuel', 'Driveline', 'Brakes', 'Chassis', 'Safety'];

export interface ItemGroup<T = ItemDto> {
  system: string;
  items: T[];
}

export function groupBySystem<T extends { system: string }>(items: T[]): ItemGroup<T>[] {
  const known: ItemGroup<T>[] = SYSTEM_ORDER.map((system) => ({
    system,
    items: items.filter((i) => i.system === system),
  }));
  // The public view types `system` as a plain string, so a value outside the
  // enum can reach here. Keep those parts visible rather than dropping them.
  const extras: ItemGroup<T>[] = [];
  for (const item of items) {
    if (SYSTEM_ORDER.some((s) => s === item.system)) continue;
    const existing = extras.find((g) => g.system === item.system);
    if (existing) existing.items.push(item);
    else extras.push({ system: item.system, items: [item] });
  }
  return [...known, ...extras].filter((g) => g.items.length > 0);
}

/** "20 passes / 3,000 mi" — the interval, spelled out. Mirrors the API's label. */
const UNIT_LABEL: Record<string, string> = {
  mi: 'mi',
  passes: 'passes',
  events: 'events',
  days: 'days',
  weeks: 'weeks',
  months: 'mo',
};

export function triggerLabels(item: ItemDto): string[] {
  return item.triggers.map((t) =>
    t.type === 'inspect'
      ? `Inspect ${t.intervalValue}d`
      : `${t.intervalValue.toLocaleString('en-US')} ${UNIT_LABEL[t.unit] ?? t.unit}`,
  );
}

/** The first (firm) occurrence for an item — the one the tile speaks about. */
export function firstOccurrence(
  occurrences: TimelineOccurrence[],
  itemId: string,
): TimelineOccurrence | undefined {
  return occurrences.find((o) => o.itemId === itemId);
}

/**
 * A single chronological spine, as the timeline screen draws it.
 *
 * Events, service crossings and date-certain expirations interleave by date.
 * Ties break by kind — the event first, then what it causes — because a service
 * that lands *on* a race day is only meaningful once you know the race is there.
 */
export type TimelineBlock =
  | { date: string; kind: 'event'; carId: string; event: TimelineResponse['events'][number] }
  | { date: string; kind: 'maint'; carId: string | null; occurrence: TimelineOccurrence }
  | { date: string; kind: 'cert'; carId: string | null; cert: CertOccurrence };

const KIND_ORDER: Record<TimelineBlock['kind'], number> = { event: 0, maint: 1, cert: 2 };

export function toBlocks(timeline: TimelineResponse): TimelineBlock[] {
  const blocks: TimelineBlock[] = [
    ...timeline.events
      .filter((e) => e.status === 'planned')
      .map((event) => ({ date: event.date, kind: 'event' as const, carId: event.carId, event })),
    ...timeline.occurrences.map((occurrence) => ({
      date: occurrence.date,
      kind: 'maint' as const,
      carId: occurrence.carId,
      occurrence,
    })),
    ...timeline.certs.map((cert) => ({ date: cert.date, kind: 'cert' as const, carId: cert.carId, cert })),
  ];

  return blocks.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : KIND_ORDER[a.kind] - KIND_ORDER[b.kind]));
}
